/**
 * Typed error shapes for the protocol boundary.
 *
 * Parse errors carry a JSON-pointer-ish path so callers (server logs, client
 * dev overlay) can point at the exact bad field without heuristics.
 */

/** One structured parse error. `path` uses zod's join style: `"a.b.0.c"`. */
export interface ParseError {
  readonly path: string;
  readonly message: string;
}

/** Result monad. No exceptions cross the parse boundary. */
export type Result<T, E> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: E };

/**
 * Runtime error thrown by branded constructors (`toTick`, `toHp`, ...) when
 * a value fails the brand's numeric/string constraints.
 *
 * Kept as an exception (not a Result) because constructors are for INTERNAL
 * callers building known-good values — hitting this is a programmer error,
 * not a boundary failure. Boundary parses use zod → Result.
 */
export class BrandError extends Error {
  public override readonly name = "BrandError";
  public readonly brand: string;
  public readonly value: unknown;

  public constructor(brand: string, value: unknown, detail: string) {
    super(`BrandError(${brand}): ${detail} (received: ${String(value)})`);
    this.brand = brand;
    this.value = value;
  }
}
